import { 
  DynamoDBClient, 
  PutItemCommand, 
  QueryCommand, 
  ScanCommand, 
  UpdateItemCommand, 
  DeleteItemCommand, 
  BatchGetItemCommand,
  AttributeValue 
} from '@aws-sdk/client-dynamodb';
import { S3Client, PutObjectCommand, DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { unmarshall, marshall } from '@aws-sdk/util-dynamodb';
import { Post, Comment } from '../models/Post';

const FEED_TABLE = 'MitchellFC_Feed';
const S3_BUCKET = 'protected-mitchellfc-docs-everyone';

// Simple UUID generator
const generateId = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export class FeedService {
  constructor(
    private ddbClient: DynamoDBClient,
    private s3Client?: S3Client
  ) {}

  async createPost(
    content: string, 
    posterUserId: string, 
    mediaFiles: File[] = []
  ): Promise<Post> {
    const postId = generateId();
    const createdAt = Date.now();
    const s3MediaKeys: string[] = [];

    if (this.s3Client && mediaFiles.length > 0) {
      for (const file of mediaFiles) {
        const key = `posts/${postId}/${generateId()}-${file.name}`;
        const body = await file.arrayBuffer();
        await this.s3Client.send(new PutObjectCommand({
          Bucket: S3_BUCKET,
          Key: key,
          Body: body as any,
          ContentType: file.type
        }));
        s3MediaKeys.push(key);
      }
    }

    const post: Post = {
      id: postId,
      createdAtEpochMilli: createdAt,
      content,
      posterCognitoUserId: posterUserId,
      s3MediaKeys: s3MediaKeys.length > 0 ? s3MediaKeys : undefined,
      likeCount: 0,
      commentCount: 0,
      recentComments: []
    };

    const item = {
      PK: `POST#${postId}`,
      SK: 'METADATA',
      GSI1PK: 'GLOBAL_FEED',
      GSI1SK: createdAt,
      GSI2PK: `USER#${posterUserId}`,
      GSI2SK: createdAt,
      ...post
    };

    await this.ddbClient.send(new PutItemCommand({
      TableName: FEED_TABLE,
      Item: marshall(item, { removeUndefinedValues: true })
    }));

    return post;
  }

  async getGlobalFeed(limit = 10, lastEvaluatedKey?: Record<string, AttributeValue>): Promise<{ posts: Post[], nextKey?: Record<string, AttributeValue> }> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      IndexName: 'GSI1',
      KeyConditionExpression: 'GSI1PK = :pk',
      ExpressionAttributeValues: marshall({
        ':pk': 'GLOBAL_FEED'
      }),
      ScanIndexForward: false,
      Limit: limit,
      ExclusiveStartKey: lastEvaluatedKey
    });

    const result = await this.ddbClient.send(command);
    const posts = (result.Items || []).map(item => unmarshall(item) as Post);

    return {
      posts,
      nextKey: result.LastEvaluatedKey
    };
  }

  async getPostMetadata(postId: string): Promise<Post | undefined> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk AND SK = :sk',
      ExpressionAttributeValues: marshall({
        ':pk': `POST#${postId}`,
        ':sk': 'METADATA'
      })
    });

    const result = await this.ddbClient.send(command);
    if (result.Items && result.Items.length > 0) {
      return unmarshall(result.Items[0]) as Post;
    }
    return undefined;
  }

  async searchPosts(query: string): Promise<Post[]> {
    const command = new ScanCommand({
      TableName: FEED_TABLE,
      FilterExpression: 'SK = :metadata AND contains(content, :query)',
      ExpressionAttributeValues: marshall({
        ':metadata': 'METADATA',
        ':query': query
      })
    });

    const result = await this.ddbClient.send(command);
    return (result.Items || []).map(item => unmarshall(item) as Post);
  }

  async toggleLike(postId: string, userId: string, isCurrentlyLiked: boolean): Promise<void> {
    if (!postId || !userId) return;
    const likeSK = `LIKE#${userId}`;
    const postPK = `POST#${postId}`;

    if (isCurrentlyLiked) {
      try {
        await this.ddbClient.send(new DeleteItemCommand({
          TableName: FEED_TABLE,
          Key: marshall({ PK: postPK, SK: likeSK })
        }));
        await this.updateLikeCount(postPK, -1);
      } catch (error: any) {
        console.error('DynamoDB DeleteItem Error Details:', error);
        throw error;
      }
    } else {
      await this.ddbClient.send(new PutItemCommand({
        TableName: FEED_TABLE,
        Item: marshall({ PK: postPK, SK: likeSK, createdAt: Date.now() })
      }));
      await this.updateLikeCount(postPK, 1);
    }
  }

  private async updateLikeCount(postPK: string, increment: number): Promise<void> {
    await this.ddbClient.send(new UpdateItemCommand({
      TableName: FEED_TABLE,
      Key: marshall({ PK: postPK, SK: 'METADATA' }),
      UpdateExpression: 'SET likeCount = if_not_exists(likeCount, :zero) + :inc',
      ExpressionAttributeValues: marshall({ ':inc': increment, ':zero': 0 })
    }));
  }

  async addComment(postId: string, userId: string, content: string): Promise<Comment> {
    const createdAt = Date.now();
    const commentId = generateId();
    const postPK = `POST#${postId}`;

    const comment: Comment = {
      id: commentId,
      postId,
      createdAtEpochMilli: createdAt,
      content,
      commentCognitoUserId: userId,
      likeCount: 0
    };

    // 1. Add the comment item
    await this.ddbClient.send(new PutItemCommand({
      TableName: FEED_TABLE,
      Item: marshall({
        PK: postPK,
        SK: `COMMENT#${createdAt}#${commentId}`,
        ...comment
      })
    }));

    // 2. Update post metadata (Count AND Recent Comments list_append)
    const getPostResult = await this.ddbClient.send(new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk AND SK = :sk',
      ExpressionAttributeValues: marshall({ ':pk': postPK, ':sk': 'METADATA' })
    }));

    if (getPostResult.Items && getPostResult.Items.length > 0) {
      const post = unmarshall(getPostResult.Items[0]) as Post;
      const recent = post.recentComments || [];
      const updatedRecent = [comment, ...recent].slice(0, 2); // Keep latest 2

      await this.ddbClient.send(new UpdateItemCommand({
        TableName: FEED_TABLE,
        Key: marshall({ PK: postPK, SK: 'METADATA' }),
        UpdateExpression: 'SET commentCount = if_not_exists(commentCount, :zero) + :inc, recentComments = :recent',
        ExpressionAttributeValues: marshall({
          ':inc': 1,
          ':zero': 0,
          ':recent': updatedRecent
        })
      }));
    }

    return comment;
  }

  async deleteComment(postId: string, commentId: string, createdAt: number): Promise<void> {
    const postPK = `POST#${postId}`;
    const commentSK = `COMMENT#${createdAt}#${commentId}`;

    // 1. Delete the comment record
    await this.ddbClient.send(new DeleteItemCommand({
      TableName: FEED_TABLE,
      Key: marshall({ PK: postPK, SK: commentSK })
    }));

    // 2. Update post metadata (Decrement count and refresh recent/top comments)
    const latestComments = await this.getComments(postId, 10);
    const updatedRecent = (latestComments.comments || []).slice(0, 2);
    
    // Find new top comment - must have at least one like
    const likedComments = latestComments.comments.filter(c => (c.likeCount || 0) > 0);
    const top = likedComments.length > 0 
      ? likedComments.reduce((prev, current) => ((prev.likeCount || 0) > (current.likeCount || 0)) ? prev : current)
      : null;

    await this.ddbClient.send(new UpdateItemCommand({
      TableName: FEED_TABLE,
      Key: marshall({ PK: postPK, SK: 'METADATA' }),
      UpdateExpression: top 
        ? 'SET commentCount = commentCount - :dec, recentComments = :recent, topComment = :top'
        : 'SET commentCount = commentCount - :dec, recentComments = :recent REMOVE topComment',
      ExpressionAttributeValues: marshall(top 
        ? { ':dec': 1, ':recent': updatedRecent, ':top': top }
        : { ':dec': 1, ':recent': updatedRecent }
      )
    }));
  }

  async getComments(postId: string, limit = 10, lastEvaluatedKey?: Record<string, AttributeValue>): Promise<{ comments: Comment[], nextKey?: Record<string, AttributeValue> }> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk AND begins_with(SK, :skPrefix)',
      ExpressionAttributeValues: marshall({
        ':pk': `POST#${postId}`,
        ':skPrefix': 'COMMENT#'
      }),
      ScanIndexForward: false, // Newest first (reverse chronological)
      Limit: limit,
      ExclusiveStartKey: lastEvaluatedKey
    });

    try {
      const result = await this.ddbClient.send(command);
      const comments = (result.Items || []).map(item => unmarshall(item) as Comment);

      return {
        comments: comments || [],
        nextKey: result.LastEvaluatedKey
      };
    } catch (error) {
      console.error('Error fetching comments:', error);
      return { comments: [], nextKey: undefined };
    }
  }

  async toggleCommentLike(postId: string, commentId: string, createdAt: number, userId: string, isCurrentlyLiked: boolean): Promise<void> {
    const likeSK = `COMMENT_LIKE#${commentId}#${userId}`;
    const postPK = `POST#${postId}`;
    const commentSK = `COMMENT#${createdAt}#${commentId}`;

    if (isCurrentlyLiked) {
      await this.ddbClient.send(new DeleteItemCommand({
        TableName: FEED_TABLE,
        Key: marshall({ PK: postPK, SK: likeSK })
      }));
      await this.updateCommentLikeCount(postPK, commentSK, -1);
    } else {
      await this.ddbClient.send(new PutItemCommand({
        TableName: FEED_TABLE,
        Item: marshall({ PK: postPK, SK: likeSK, createdAt: Date.now() })
      }));
      await this.updateCommentLikeCount(postPK, commentSK, 1);
    }

    // Refresh Top Comment on Post
    await this.refreshTopComment(postId);
  }

  private async updateCommentLikeCount(postPK: string, commentSK: string, increment: number): Promise<void> {
    await this.ddbClient.send(new UpdateItemCommand({
      TableName: FEED_TABLE,
      Key: marshall({ PK: postPK, SK: commentSK }),
      UpdateExpression: 'SET likeCount = if_not_exists(likeCount, :zero) + :inc',
      ExpressionAttributeValues: marshall({ ':inc': increment, ':zero': 0 })
    }));
  }

  private async refreshTopComment(postId: string): Promise<void> {
    const postPK = `POST#${postId}`;
    // Fetch a sample of comments to find the most liked
    const result = await this.getComments(postId, 50);
    
    // Find new top comment - must have at least one like
    const likedComments = (result.comments || []).filter(c => (c.likeCount || 0) > 0);
    const top = likedComments.length > 0 
      ? likedComments.reduce((prev, current) => ((prev.likeCount || 0) > (current.likeCount || 0)) ? prev : current)
      : null;

    if (top) {
      await this.ddbClient.send(new UpdateItemCommand({
        TableName: FEED_TABLE,
        Key: marshall({ PK: postPK, SK: 'METADATA' }),
        UpdateExpression: 'SET topComment = :top',
        ExpressionAttributeValues: marshall({ ':top': top })
      }));
    } else {
      // If no comments have likes, clear the top comment
      await this.ddbClient.send(new UpdateItemCommand({
        TableName: FEED_TABLE,
        Key: marshall({ PK: postPK, SK: 'METADATA' }),
        UpdateExpression: 'REMOVE topComment',
      }));
    }
  }

  async checkIfCommentLiked(postId: string, commentId: string, userId: string): Promise<boolean> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk AND SK = :sk',
      ExpressionAttributeValues: marshall({
        ':pk': `POST#${postId}`,
        ':sk': `COMMENT_LIKE#${commentId}#${userId}`
      })
    });

    const result = await this.ddbClient.send(command);
    return (result.Items || []).length > 0;
  }

  async checkIfLiked(postId: string, userId: string): Promise<boolean> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk AND SK = :sk',
      ExpressionAttributeValues: marshall({
        ':pk': `POST#${postId}`,
        ':sk': `LIKE#${userId}`
      })
    });

    const result = await this.ddbClient.send(command);
    return (result.Items || []).length > 0;
  }

  async getLikeStatuses(postIds: string[], userId: string): Promise<Set<string>> {
    if (postIds.length === 0) return new Set();
    const keys = postIds.map(id => marshall({ PK: `POST#${id}`, SK: `LIKE#${userId}` }));
    const result = await this.ddbClient.send(new BatchGetItemCommand({
      RequestItems: { [FEED_TABLE]: { Keys: keys, ProjectionExpression: 'PK' } }
    }));
    const likedSet = new Set<string>();
    (result.Responses?.[FEED_TABLE] || []).forEach(item => {
      const pk = unmarshall(item).PK as string;
      likedSet.add(pk.replace('POST#', ''));
    });
    return likedSet;
  }

  async signUrls(posts: Post[]): Promise<Post[]> {
    if (!this.s3Client) return posts;
    return await Promise.all(posts.map(async (post) => {
      if (post.s3MediaKeys && post.s3MediaKeys.length > 0) {
        const urls = await Promise.all(post.s3MediaKeys.map(async (key) => {
          try {
            const command = new GetObjectCommand({ Bucket: S3_BUCKET, Key: key });
            return await getSignedUrl(this.s3Client!, command, { expiresIn: 3600 });
          } catch (e) { return ''; }
        }));
        return { ...post, signedMediaUrls: urls };
      }
      return post;
    }));
  }

  async getUserPosts(userId: string, limit = 10, lastEvaluatedKey?: Record<string, AttributeValue>): Promise<{ posts: Post[], nextKey?: Record<string, AttributeValue> }> {
    const command = new QueryCommand({
      TableName: FEED_TABLE,
      IndexName: 'GSI2',
      KeyConditionExpression: 'GSI2PK = :pk',
      ExpressionAttributeValues: marshall({
        ':pk': `USER#${userId}`
      }),
      ScanIndexForward: false,
      Limit: limit,
      ExclusiveStartKey: lastEvaluatedKey
    });

    const result = await this.ddbClient.send(command);
    const posts = (result.Items || []).map(item => unmarshall(item) as Post);

    return {
      posts,
      nextKey: result.LastEvaluatedKey
    };
  }

  async deletePost(post: Post): Promise<void> {
    const postPK = `POST#${post.id}`;
    const query = await this.ddbClient.send(new QueryCommand({
      TableName: FEED_TABLE,
      KeyConditionExpression: 'PK = :pk',
      ExpressionAttributeValues: marshall({ ':pk': postPK })
    }));
    if (query.Items) {
      for (const item of query.Items) {
        await this.ddbClient.send(new DeleteItemCommand({
          TableName: FEED_TABLE,
          Key: marshall({ PK: postPK, SK: unmarshall(item).SK })
        }));
      }
    }
    if (this.s3Client && post.s3MediaKeys) {
      for (const key of post.s3MediaKeys) {
        await this.s3Client.send(new DeleteObjectCommand({ Bucket: S3_BUCKET, Key: key }));
      }
    }
  }
}
